// Conversations: the lists, the decrypted chat, what the host sees, questions and answers, and
// the errors the browser itself raises (the API's own messages are shown as sent).
import type { ReactNode } from "react";

import { plural, rich } from "../../helpers";

const p = plural("en");

export default {
  title: "Conversations",
  conversation: "Conversation",
  description: "Encrypted inside the worker to your devices' keys; the host sees metadata only.",
  emptyTitle: "No conversations",
  emptyBody: "A conversation is created for every run and for every interactive workspace session.",
  columns: { conversation: "Conversation", kind: "Kind", about: "About", created: "Created", lastEvent: "Last event" },
  kinds: { run: "run", ticket: "ticket", workspace: "workspace", task: "task", group: "task group chat" },
  aboutRun: (id: string) => `run ${id}`,
  aboutWorkspace: (id: string) => `workspace ${id}`,
  linkFirst: (link: ReactNode) =>
    rich("Conversations are encrypted inside the worker to your devices' keys and listed only to a device session. ", link, " to read them here."),
  linkThisBrowser: "Link this browser",
  checkingKeys: "Checking this browser's keys…",
  detail: {
    facts: "Conversation facts",
    relay: "Relay",
    worker: "Worker",
    connected: "connected",
    offline: "offline",
    events: "Events",
    eventsDescription: "Decrypted in this browser with its device key",
  },
  relayStatus: { idle: "idle", connecting: "connecting", open: "open", closed: "closed" },
  showHost: "Show what the host sees",
  card: {
    onlyYourDevices: "Only your devices hold the key",
    readableOn: (n: number) => p(n, { one: "Readable on # device", other: "Readable on # devices" }),
    noneTitle: "No conversation yet",
    noneBody: "The worker opens one when the agent starts; its events arrive here as they happen.",
    noQuestion: "No question is waiting for you on this run.",
    locked: (link: ReactNode) =>
      rich(
        "The prompt, the agent's replies and its questions are encrypted inside the worker to your devices' keys. This browser is not one of them yet: ",
        link,
        " to read the conversation and answer here.",
      ),
    linkThisBrowser: "link this browser",
  },
  events: {
    emptyTitle: "No events yet",
    emptyBody: "Events arrive over the relay as the worker produces them.",
    segment: (index: number) => `Segment ${index}`,
    count: (n: number) => p(n, { one: "# event", other: "# events" }),
    closedAt: (seq: number) => `closed at ${seq}`,
    open: "open",
    wrapped: (devices: number, version: number) =>
      p(devices, { one: `content key wrapped to # device in recipient list v${version}`, other: `content key wrapped to # devices in recipient list v${version}` }),
    key: { pending: "unwrapping key…", missing: "not encrypted to this browser", unwrapped: "decrypted here" },
    you: "You",
    own: { prompt: "workflow prompt", answer: "answer", instruction: "instruction" },
    result: "result",
    tool: "tool",
    options: (options: string) => `Options: ${options}`,
    couldNotDecrypt: (reason: string) => `Could not decrypt: ${reason}`,
    noKey: "Encrypted — this browser does not hold the key for this segment.",
    showWorking: "Show working",
    showWorkingHint: "Show the agent's interim messages and tool calls as rows. Off, they are folded into Thinking, so the prompt and the answer stand out.",
    raw: "Raw",
    formatted: "Formatted",
    copy: "Copy",
    copied: "Copied",
  },
  thinking: {
    title: "Thinking",
    live: "Thinking…",
    updates: (n: number) => p(n, { one: "# update", other: "# updates" }),
    view: "View",
    viewLabel: "View the agent's thinking",
    dialogHint: "What the agent thought and did on the way to its answer, decrypted in this browser.",
    redacted: "The model kept this part of its thinking private.",
    streaming: "streaming",
    tool: "Tool",
    system: "System",
  },
  host: {
    intro: (capture: ReactNode) =>
      rich(
        "This is the whole record on the host: ids, types, times, tool names and sizes. The content was encrypted inside the worker by ",
        capture,
        " to your devices' keys; the host has no key that opens it.",
      ),
    noEvents: "No events have reached this browser yet.",
    bytes: (n: number) => `${n} B`,
  },
  question: {
    label: "Question",
    needsApproval: "The agent needs your approval",
    waiting: "The agent is waiting for you",
    asked: (time: string) => `asked ${time}`,
    checkpointed: "worker checkpointed, no CPU held",
    decryptedHere: "Decrypted on this device",
    unlinked: (link: ReactNode) => rich("The question text is encrypted to your devices. ", link, " to read and answer it here."),
    noConversation: "This question has no conversation to answer in.",
    delivered: "Answer delivered to the worker",
    queued: "Answer queued; the worker is not connected",
    decrypting: "Decrypting the question in this browser…",
    answered: (answer: string) => `Answered “${answer}”. Encrypted to the worker and signed by this browser; the run resumes in a moment.`,
    form: "Answer",
    yourAnswer: "Your answer",
    ownAnswer: "Or write your own answer…",
    typeAnswer: "Type your answer…",
    sending: "Sending…",
    answer: "Answer",
    footnote: "Your answer is encrypted in this browser and signed with its device key. The host relays it but cannot read it.",
  },
  errors: {
    noWritableSegment: "this browser holds no key for an open segment of the conversation",
    notAcknowledged: "relay did not acknowledge the message",
    apiUnreachable: "ocs-api is not reachable",
    apiStatus: (status: number) => `ocs-api returned ${status}`,
  },
};
